import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Anchor,
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  costFormater,
  NewTabIcon,
  PriceInput,
  RawButton,
  SimpleHint,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { useGetFundIcon } from '@/modules/investments/hooks';

import useInstrumentCategoriesQuery from '../../queries/useInstrumentCategories.query';
import { FundPayload } from '../../SelfDirected.types';

import { PERCENTS_FULL_LIQUIDATION, PERCENTS_LIQUIDATION_LIMIT } from './Funds.constants';
import LiquidationCheckbox from './LiquidationCheckbox';

const Wrapper = styled(Box)`
  transition: all 0.25s;

  .liquidation-input {
    font-weight: 700;
  }
`;

interface Props extends FundPayload {
  isPendingLiquidation?: boolean;
  isLiquidationAllMode?: boolean;
  value: number;
  onChangeValue: (value: number) => void;
  testId?: string;
  isLiquidation?: boolean;
  onSetLiquidation: (value: boolean) => void;
}

const FundsItem: React.FC<Props> = ({
  logo,
  id,
  title,
  stockCode,
  status,
  link,
  election,
  amount,
  category,
  assetClass,
  isLiquidationAllMode,
  isLiquidation = false,
  onSetLiquidation,
  isPendingLiquidation,
  value,
  onChangeValue,
  testId,
}) => {
  const { isMobile } = useResponsive();
  const [liquidationAmount, setLiquidationAmount] = useState('');
  const { t } = useTranslation();
  const { dataMap } = useInstrumentCategoriesQuery();
  const currentCategory = useMemo(() => _.get(dataMap, `${[category]}`, '') as string, [category, dataMap]);

  const [expanded, setExpanded] = useState(false);
  const handleToggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const amountNumber = useMemo(() => {
    const newValueNumber = _.toNumber(amount);
    if (_.isNaN(newValueNumber)) {
      return 0;
    }
    return newValueNumber;
  }, [amount]);

  const liquidationNumber = useMemo(() => {
    const currentValue = _.toNumber(liquidationAmount);
    if (_.isNaN(currentValue)) {
      return 0;
    }
    return currentValue;
  }, [liquidationAmount]);

  useEffect(() => {
    if (value !== liquidationNumber) {
      onChangeValue(liquidationNumber);
    }
  }, [liquidationNumber, onChangeValue, value]);

  useEffect(() => {
    const currentAmount = parseFloat(liquidationAmount);
    if (_.isNaN(currentAmount)) {
      return;
    }
    if (amount === currentAmount) {
      onSetLiquidation(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidationAmount]);

  useEffect(() => {
    if (isLiquidationAllMode) {
      setLiquidationAmount(`${amountNumber}`);
    } else {
      setLiquidationAmount('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiquidationAllMode]);

  const handleChangeValue = useCallback(
    (newValue: string) => {
      const newValueNumber = _.toNumber(newValue);

      if (newValueNumber > amountNumber) {
        setLiquidationAmount(`${amountNumber}`);
        return;
      }
      setLiquidationAmount(newValue);
    },
    [amountNumber]
  );

  const isLiquidationApplied = useMemo(() => {
    const percents = liquidationNumber / amountNumber;
    if (amountNumber === 0) {
      return true;
    }
    if (_.isNaN(percents)) {
      return true;
    }
    return percents >= PERCENTS_LIQUIDATION_LIMIT && percents !== PERCENTS_FULL_LIQUIDATION;
  }, [amountNumber, liquidationNumber]);

  const handleToggleCheck = useCallback(
    (value: boolean) => {
      onSetLiquidation(value);
      if (value) {
        setLiquidationAmount(`${amount}`);
        return;
      }
      setLiquidationAmount('');
    },
    [amount, onSetLiquidation]
  );
  const handleGetFundIcon = useGetFundIcon();

  if (isMobile) {
    return (
      <Wrapper
        data-testid={testId}
        direction="column"
        background="canvas"
        round="container2Round"
        border={{ size: 'small', color: 'border1' }}
        margin={{ top: 'spacing8' }}
        pad="spacing12"
      >
        <Box direction="row" align="center" justify="start">
          <Box direction="row" align="center" justify="center">
            <Box margin={{ left: 'spacing12' }} style={{ opacity: isPendingLiquidation ? 0.2 : 1 }}>
              {handleGetFundIcon({
                image: logo,
                status: status,
                isGreyMode: false,
                isNullMode: true,
              })}
            </Box>
            <Box
              margin={{ left: 'spacing12', right: 'spacing12' }}
              style={{
                flex: 2,
                position: 'relative',
              }}
              direction="column"
            >
              <Box width="100%">
                <Text
                  data-testid={`${testId}_title`}
                  tip={title}
                  title={title}
                  color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
                  truncate
                  weight={700}
                >
                  {title}
                </Text>
                <Text
                  data-testid={`${testId}_stock`}
                  color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
                  title={`${stockCode || ''} ${currentCategory ? `, ${currentCategory}` : ''}`}
                  truncate
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {stockCode}

                  {currentCategory ? `, ${currentCategory}` : ''}
                </Text>
              </Box>
            </Box>
          </Box>
          <div>
            <Box direction="column-reverse" align="end">
              <Box>
                <Text
                  style={{ whiteSpace: 'nowrap' }}
                  data-testid={`${testId}_elections`}
                  color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
                >
                  Election: {`${election}%`}
                </Text>
              </Box>
              <Box>
                <Text
                  data-testid={`${testId}_amount`}
                  weight={700}
                  color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
                >
                  {costFormater(amount, { isPrecision: true })}
                </Text>
              </Box>
            </Box>
          </div>
        </Box>
        <Box height="1px" background="border1" margin={{ vertical: 'spacing12' }} />
        <Box direction="column">
          <Box direction="row" align="center">
            <Box fill margin={{ right: 'spacing12' }}>
              <PriceInput
                className="liquidation-input"
                testId={`sell-investments_${id}`}
                name={`sell investments ${id} price input`}
                disabled={isLiquidationAllMode || isLiquidation || isPendingLiquidation}
                value={
                  isPendingLiquidation
                    ? ''
                    : isLiquidationAllMode || isLiquidation
                      ? `${amount}`
                      : liquidationAmount
                }
                onChange={handleChangeValue}
                placeholder="$0.00"
              />
            </Box>
            {link ? (
              <Anchor data-testid={`${testId}_open_link`} target="_blank" href={link}>
                <Box align="center">
                  <NewTabIcon color="iconSecondary" size="28px" />
                </Box>
              </Anchor>
            ) : null}
          </Box>
          <Box direction="row" align="center" justify="start" margin={{ top: 'spacing12' }}>
            {isPendingLiquidation ? (
              <SimpleHint
                content={
                  <Text size="small">
                    {t("This position is being liquidated right now. You can't sell this item.")}
                  </Text>
                }
              >
                <LiquidationCheckbox
                  value={false}
                  id={id}
                  testId="sell-investments_liquidation"
                  onChange={() => {}}
                  isLiquidationMode={isLiquidationApplied}
                  disabled
                />
              </SimpleHint>
            ) : (
              <LiquidationCheckbox
                testId="sell-investments_liquidation"
                id={id}
                value={isLiquidation}
                onChange={handleToggleCheck}
                isLiquidationMode={isLiquidationApplied}
                disabled={isLiquidationAllMode}
              />
            )}
          </Box>
        </Box>
      </Wrapper>
    );
  }
  return (
    <Wrapper
      data-testid={testId}
      direction="column"
      pad={{ vertical: '17px', horizontal: 'spacing24' }}
      background="canvas"
      round="container2Round"
      border={{ size: 'small', color: 'border1' }}
      margin={{ top: 'xs' }}
    >
      <Box direction="row" align="center" justify="start">
        <RawButton data-testid={`${testId}_toggle-button`} onClick={handleToggleExpanded}>
          <Box width="24px" height="24px">
            {expanded ? <ArrowUpIcon color="iconPrimary" /> : <ArrowDownIcon color="iconPrimary" />}
          </Box>
        </RawButton>
        <Box margin={{ left: 's' }} style={{ opacity: isPendingLiquidation ? 0.2 : 1 }}>
          {handleGetFundIcon({
            image: logo,
            status: status,
            isGreyMode: false,
            isNullMode: true,
          })}
        </Box>
        <Box
          margin={{ left: 's' }}
          style={{
            flex: 2,
            position: 'relative',
          }}
          direction="column"
        >
          <Box width="100%">
            <Text
              data-testid={`${testId}_title`}
              tip={title}
              color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
              truncate
              weight={700}
            >
              {title}
            </Text>
            <Text data-testid={`${testId}_stock`} color={isPendingLiquidation ? 'textDisabled' : 'textBody'}>
              {stockCode}
            </Text>
          </Box>
        </Box>
        <Box
          margin={{ left: 's' }}
          style={{
            width: '24px',
          }}
          direction="column"
        >
          {link ? (
            <Anchor data-testid={`${testId}_open_link`} target="_blank" href={link}>
              <Box align="center">
                <NewTabIcon color="iconSecondary" size="26px" />
              </Box>
            </Anchor>
          ) : (
            <Box width="26px" />
          )}
        </Box>
        <Box width="102px" margin={{ left: 'l' }}>
          <Text
            data-testid={`${testId}_elections`}
            color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
          >
            {`${election}%`}
          </Text>
        </Box>
        <Box width={{ min: '110px' }} margin={{ left: 'l' }}>
          <Text
            data-testid={`${testId}_amount`}
            weight={700}
            color={isPendingLiquidation ? 'textDisabled' : 'textBody'}
          >
            {costFormater(amount, { isPrecision: true })}
          </Text>
        </Box>
        <Box width={{ min: '150px', max: '150px' }} margin={{ left: 'l' }}>
          <PriceInput
            className="liquidation-input"
            testId={`${testId}_price`}
            name={`sell investments ${id} price input`}
            disabled={isLiquidationAllMode || isLiquidation || isPendingLiquidation}
            value={
              isPendingLiquidation
                ? ''
                : isLiquidationAllMode || isLiquidation
                  ? `${amount}`
                  : liquidationAmount
            }
            onChange={handleChangeValue}
            placeholder="$0.00"
          />
        </Box>

        <Box direction="row" align="center" justify="end" margin={{ left: 'xs' }} style={{ flex: 1.2 }}>
          {isPendingLiquidation ? (
            <SimpleHint
              testId={`${testId}_liquidation-hint`}
              content={
                <Text size="small" data-testid={`${testId}_liquidation-hint_text`}>
                  {t("This position is being liquidated right now. You can't sell this item.")}
                </Text>
              }
            >
              <LiquidationCheckbox
                value={false}
                id={id}
                testId={`${testId}_liquidation`}
                onChange={() => {}}
                isLiquidationMode={isLiquidationApplied}
                disabled
              />
            </SimpleHint>
          ) : (
            <LiquidationCheckbox
              testId={`${testId}_liquidation`}
              id={id}
              value={isLiquidation}
              onChange={handleToggleCheck}
              isLiquidationMode={isLiquidationApplied}
              disabled={isLiquidationAllMode}
            />
          )}
        </Box>
      </Box>

      {expanded && (
        <>
          <Box height="1px" background="border1" margin={{ top: 'l', bottom: 'l' }}></Box>
          <Box direction="row" align="center">
            {currentCategory && (
              <Box direction="column" width="150px" margin={{ right: 'l' }}>
                <Text size="small" weight={700} color="textBody" margin={{ bottom: 'xxxs' }}>
                  Category
                </Text>
                <Box pad={{ vertical: 'xxxs' }}>
                  <Text data-testid={`${testId}_category`} color="textBody" truncate size="medium">
                    {currentCategory}
                  </Text>
                </Box>
              </Box>
            )}

            <Box direction="column" width="150px" margin={{ right: 'l' }}>
              <Text size="small" weight={700} color="textBody" margin={{ bottom: 'xxxs' }}>
                {t('Asset class')}
              </Text>
              <Box pad={{ vertical: 'xxxs' }}>
                <Text data-testid={`${testId}_assets`} color="textBody" size="medium">
                  {assetClass}
                </Text>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(FundsItem);
